/**
 * SkillDelivery Platform — WMS Showcase
 * Copyright © 2026 SKD RATWANI Technology & Software Solutions Pvt Ltd
 * All Rights Reserved.
 * PROPRIETARY AND CONFIDENTIAL
 *
 * Content authored by Lalit Ratwani, drawing from 22+ years of
 * Manhattan WMS implementation practice (2003-2026).
 *
 * "Manhattan WMS Capabilities" section embedded in /domain/wms.
 * Reads showcase_modules from V2 Supabase. Hides itself entirely
 * when no published modules exist so the host page stays clean.
 */

import React, { useEffect, useState } from 'react';
import KnowledgeModuleCard from './KnowledgeModuleCard';
import VersionTimeline from './VersionTimeline';
import v2Supabase from '../../lib/v2Supabase';

const SkeletonCard = () => (
  <div className="bg-white border border-brand-border rounded-brandCard p-6 animate-pulse">
    <div className="w-11 h-11 rounded-xl bg-brand-bgOff2 mb-4" />
    <div className="h-5 bg-brand-bgOff2 rounded w-2/3 mb-2" />
    <div className="h-3 bg-brand-bgOff2 rounded w-1/2 mb-4" />
    <div className="space-y-2">
      <div className="h-3 bg-brand-bgOff2 rounded" />
      <div className="h-3 bg-brand-bgOff2 rounded w-5/6" />
      <div className="h-3 bg-brand-bgOff2 rounded w-4/6" />
    </div>
  </div>
);

const ManhattanKnowledgeShowcase = () => {
  const [status, setStatus] = useState('loading'); // loading | ok | error
  const [modules, setModules] = useState([]);

  useEffect(() => {
    let cancelled = false;
    (async () => {
      const { data, error } = await v2Supabase
        .from('showcase_modules')
        .select('*')
        .eq('domain', 'wms')
        .eq('is_published', true)
        .order('sequence_number')
        .execute();
      if (cancelled) return;
      if (error) {
        // eslint-disable-next-line no-console
        console.error('[ManhattanKnowledgeShowcase] load failed:', error);
        setStatus('error');
        return;
      }
      setModules(data || []);
      setStatus('ok');
    })();
    return () => {
      cancelled = true;
    };
  }, []);

  // Hide the entire section when nothing is published — keeps /domain/wms
  // clean while content is still being seeded.
  if (status === 'ok' && modules.length === 0) return null;

  return (
    <section className="mt-20" aria-label="Manhattan WMS capabilities">
      <p className="text-center text-[11px] font-bold tracking-[2px] uppercase text-brand-cyan">
        Deep dive
      </p>
      <h2 className="mt-3 text-center text-3xl sm:text-4xl font-extrabold tracking-tighter leading-tight bg-gradient-to-br from-brand-cyan to-brand-blue bg-clip-text text-transparent">
        Manhattan WMS Capabilities
      </h2>
      <p className="mt-4 text-center text-base text-brand-muted leading-relaxed max-w-2xl mx-auto">
        Six core modules. Twenty-two years of practice. Every screen
        you'll see in a real Manhattan implementation.
      </p>

      <div className="mt-10 grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4 sm:gap-5">
        {status === 'loading' && (
          <>
            <SkeletonCard />
            <SkeletonCard />
            <SkeletonCard />
          </>
        )}
        {status === 'ok' &&
          modules.map((m) => <KnowledgeModuleCard key={m.id} module={m} />)}
      </div>

      {status === 'error' && (
        <div className="mt-8 text-center bg-white border border-brand-border rounded-brandCard p-6 max-w-xl mx-auto">
          <p className="text-sm text-brand-muted">
            Couldn't load capability detail right now. The capabilities
            are still there — please refresh.
          </p>
        </div>
      )}

      {status === 'ok' && modules.length > 0 && <VersionTimeline />}
    </section>
  );
};

export default ManhattanKnowledgeShowcase;
